import MappingVisitor from "data/util/MappingVisitor";
import {
    FT_CHILD,
    MAP_PLACEHOLDER,
    MAP_REFERENCE_GLOBAL,
    MAP_TARGET,
} from "data/meta/MappingInfoStore";
import entityMetaStore from "data/meta/entityMetaStore";
import TableColumnMetaStore from "data/meta/TableColumnMetaStore";
import {
    ERR_MULTIPLE_MATCHES_FOUND,
    ERR_NO_MATCH_FOUND,
    isExcluded,
} from "data/MappingStore";
import Dispatcher from "Dispatcher";
import MapDialogActions from "data/MapDialogActions";
import isFieldHidden from "data/util/isFieldHidden";
import isMoment from "data/util/isMoment";

class BuildMappingTreeVisitor extends MappingVisitor {
    constructor(state, access, snapshot) {
        super(state, access, snapshot);
        this.stack = [];
    }

    execute(ctx) {
        const treeQueue = [];
        let hasQueue;
        do {
            this._enterFrame();
            super.execute(ctx);
            const stateTree = this._leaveFrame().children[0];
            treeQueue.push(stateTree);
            if(this.state.queue && this.state.queue.length > 0) {
                hasQueue = true;
                // get the next mapping state off the queue.
                this.state = this.state.processQueueCallback(this.state);
            }
            else {
                hasQueue = false;
            }
        }
        while(hasQueue); // loop over each queued mapping state
        this.state = this._initialState; // reset the state

        if(treeQueue.length > 1) {
            const children = [];
            const distinctColumnNames = new Set();
            treeQueue.forEach(it => {
                it.children.forEach(field => {
                    const tk = entityMetaStore.getInfo(field.info.entityKey).tableKey;
                    const ti = TableColumnMetaStore.getInfoByKey(tk, field.info.logicalKey);
                    if(ti.derivation != null) return; // don't show derived fields.
                    if (!distinctColumnNames.has(field.info.logicalColumn)) {
                        distinctColumnNames.add(field.info.logicalColumn);
                        children.push(field);
                    }
                });
            });
            // sort the field labels by natural order.
            children.sort((a, b) => a.label.localeCompare(
                    b.label,
                    navigator.languages[0] || navigator.language,
                    {
                        numeric: true,
                        ignorePunctuation: true
                    }
                ));

            this.tree = {
                ...treeQueue[treeQueue.length-1], // use the first state as the root
                children,
            };
        }
        else {
            this.tree = treeQueue[treeQueue.length-1];
        }
        return this;
    }

    getTree() {
        return this.tree;
    }

    _enterFrame(item) {
        if (item == null) {
            item = {
                children: [],
            };
        }
        this.stack.unshift(item);
        this.item = this.stack[0];
        this.children = this.item.children;
    }

    _leaveFrame() {
        const item = this.item;
        this.stack.shift();
        if (this.stack.length === 0) {
            delete this.item;
            delete this.children;
        } else {
            this.item = this.stack[0];
            this.children = this.item.children;
            this.children.push(item);
        }
        return item;
    }

    doRecord(ctx, target, excluded, source, type, edgeInfo) {
        this._enterFrame();
        super.doRecord(ctx, target, excluded, source, type, edgeInfo);
        const label = target.getLabel();
        Object.assign(this.item, {
            label,
            type,
            active: !excluded,
            source,
            target,
            cid: target.id,
        });
        if (type === MAP_TARGET) {
            this.item.shortname = "TARGET";
            this.item.forceActive = "The mapping target may not be excluded.";
        } else {
            this.item.shortname = edgeInfo.key;
        }
        if (this.magicPointerKey != null) {
            const i = this.children.findIndex(
                it => it.shortname === this.magicPointerKey);
            if (i >= 0) {
                const mr = this.children[i];
                this.children.splice(i, 1);
                this.item.shortname = mr.shortname; // this doesn't make any sense. except "backwards compatible". :|
                mr.children.push(this.item);
                mr.isMagicRoot = true;
                this.item = mr;
                this.children = mr.children;
            }
        }
        return this._leaveFrame();
    }

    doMagicRoot(ctx, target, excluded, fieldInfo, sourceValue) {
        super.doMagicRoot(ctx, target, excluded, fieldInfo, sourceValue);
        this.magicPointerKey = fieldInfo.key;
    }

    doGlobalAssoc(
        ctx,
        target,
        excluded,
        fieldInfo,
        sourceValue,
        createIfMissing,
    ) {
        if (isFieldHidden(target.entityKey, fieldInfo)) return;
        const cmi = ctx.childMappingInfo;
        const targetInfo = entityMetaStore.getInfo(cmi.targetKey);
        const kids = this.children;
        this.__forEach(sourceValue, it => {
            const lo = this.__getTargetIdLO(it, target, fieldInfo);
            const skeleton = {
                shortname: fieldInfo.key,
                active: true,
                type: MAP_PLACEHOLDER,
                forceActive: false,
                children: [],
                loadObject: lo,
                cid: `${target.id}-${fieldInfo.key}-${it.id}`,
                info: fieldInfo,
                exclusionInfo: buildExclusionInfo(target.id, fieldInfo.logicalColumn),
            };
            if (fieldInfo.multiValued) {
                skeleton.forceActive = "Multi-select lists may not be excluded.";
            }
            if (lo.hasValue()) {
                const id = lo.getValueEnforcing();
                const ep = this.access.getEntityProxy(
                    fieldInfo.otherEntityKey,
                    id,
                );
                // this.doRecord(ctx, ep, it, cmi.type, fieldInfo);
                const label = ep.getLabel();
                kids.push({
                    ...skeleton,
                    type: MAP_REFERENCE_GLOBAL,
                    label: `${fieldInfo.name}: ${label}`,
                    active: !isExcluded(this.state, target.id, fieldInfo.logicalColumn, id),
                    exclusionInfo: buildExclusionInfo(target.id, fieldInfo.logicalColumn, id),
                    toggleActive: fieldInfo.multiValued
                        ? null
                        : () => toggleExclusion(target.id, fieldInfo.logicalColumn, id),
                });
            } else if (lo.hasError()) {
                const err = lo.getErrorEnforcing();
                switch (err) {
                    case ERR_MULTIPLE_MATCHES_FOUND:
                        kids.push({
                            ...skeleton,
                            label: `Multiple matching ${targetInfo.pluralName} found, leaving prior value.`,
                        });
                        break;
                    case ERR_NO_MATCH_FOUND:
                        kids.push({
                            ...skeleton,
                            label: `No matching ${targetInfo.name} was found for ${it.getLabel()}.`,
                        });
                        break;
                    default:
                        console.error(
                            "Error loading",
                            fieldInfo.otherEntityKey,
                            it,
                            err,
                        );
                        break;
                }
            } else {
                // this might be a lie, but it'll be true here in a sec. :)
                kids.push({
                    ...skeleton,
                    label: `Loading "${targetInfo.pluralName}" list...`,
                });
            }
        });
    }

    doComposedChild(ctx, targetParent, edgeInfo, target, source) {
        if (isFieldHidden(targetParent.entityKey, edgeInfo)) return;
        super.doComposedChild(ctx, targetParent, edgeInfo, target, source);
        const item = this.item.children[this.item.children.length - 1];
        item.label = edgeInfo.name + ": " + item.label;
        item.exclusionInfo = buildExclusionInfo(targetParent.id, edgeInfo.logicalColumn, target.id);
        item.toggleActive = () =>
            toggleExclusion(targetParent.id, edgeInfo.logicalColumn, target.id);
    }

    doExcludedChild(ctx, targetParent, edgeInfo, targetId, source) {
        if (isFieldHidden(targetParent.entityKey, edgeInfo)) return;
        super.doExcludedChild(ctx, targetParent, edgeInfo, targetId, source);
        this.children.push({
            shortname: edgeInfo.key,
            label: edgeInfo.name + ": " + source.getLabel(),
            type: FT_CHILD,
            active: false,
            exclusionInfo: buildExclusionInfo(targetParent.id, edgeInfo.logicalColumn, targetId),
            toggleActive: () =>
                toggleExclusion(targetParent.id, edgeInfo.logicalColumn, targetId),
            source,
            target: null,
            cid: targetId,
        });
    }

    doField(ctx, target, excluded, fieldInfo, sourceValue) {
        super.doField(ctx, target, excluded, fieldInfo, sourceValue);
        if (isFieldHidden(target.entityKey, fieldInfo)) return;
        const oldValue = this.__oldValue(target, fieldInfo);
        this.children.push({
            shortname: fieldInfo.key,
            label: fieldInfo.name,
            sourceValue,
            targetValue: oldValue,
            value: toDisplayValue(fieldInfo, sourceValue),
            old: toDisplayValue(fieldInfo, oldValue),
            type: fieldInfo.type,
            info: fieldInfo, // todo: what is this for?
            fieldEnabled: target.isFieldEnabled(fieldInfo),
            children: [], // always empty....
            active: !excluded,
            exclusionInfo: buildExclusionInfo(target.id, fieldInfo.logicalColumn),
            toggleActive: () =>
                toggleExclusion(target.id, fieldInfo.logicalColumn),
            cid: `${target.entityKey}-${target.id}-${fieldInfo.key}`,
        });
    }

    doAutomanaged(
        ctx,
        target,
        excluded,
        fieldInfo,
        autoManagedByFieldInfo,
        sourceValue,
    ) {
        this.__forEach(sourceValue, it => {
            const t = this.__getAutomanagedTarget(target, it, fieldInfo);
            if (t == null) return;
            const item = this.doRecord(ctx, t, false, it, FT_CHILD, fieldInfo);
            item.label = fieldInfo.name + ": " + item.label;
            item.forceActive = "Managed records may not be excluded.";
            const amfItem = item.children.find(c =>
                c.shortname === autoManagedByFieldInfo.key);
            if (amfItem != null) {
                amfItem.forceActive = "Management records may not be excluded.";
            }
        });
    }
}

const buildExclusionInfo = (recordId, columnKey, itemKey) => ({
    recordId, columnKey, itemKey,
});

const toggleExclusion = (recordId, columnKey, itemKey) => Dispatcher.dispatch({
    type: MapDialogActions.TOGGLE_EXCLUSION,
    recordId,
    columnKey,
    itemKey,
});

// from FieldMapping
export const toDisplayValue = (info, value) => {
    if (value == null) {
        return "";
    } else if (info.type === "DateField") {
        return moment(value).format("MM/DD/YYYY");
    } else if (info.type === "DateTimeField") {
        return moment(value).format("MM/DD/YYYY HH:mm");
    } else if (info.type === "TimeField") {
        return moment(value).format("HH:mm");
    } else if (isMoment(value)) {
        return moment(value).format("MM/DD/YYYY HH:mm");
    } else if (info.type === "ChoiceField") {
        return value instanceof Array ? value.join(", ") : value;
    } else {
        return value;
    }
};

export default BuildMappingTreeVisitor;



// WEBPACK FOOTER //
// ./app/data/selectors/BuildMappingTreeVisitor.js